// SKUs and EAN / GTIN (Stage 2.6A), shared by the server and the screens.
// SKU = [Product code][sep][Colour code][sep][Size code] in the organisation's order, e.g. ARC014-SH-IFR-EARC-OB-S.

export type SkuPart = "product" | "colour" | "size";
export interface SkuFormat { separator: string; order: SkuPart[] }
export const DEFAULT_SKU_FORMAT: SkuFormat = { separator: "-", order: ["product", "colour", "size"] };
export const SKU_SEPARATORS = ["-", "_", ".", "/"] as const;
export const SKU_ORDERS: Array<[string, SkuPart[], string]> = [
  ["pcs", ["product", "colour", "size"], "Product code · Colour code · Size code"],
  ["psc", ["product", "size", "colour"], "Product code · Size code · Colour code"],
];

export function normalizeSkuFormat(v: any): SkuFormat {
  const separator = SKU_SEPARATORS.includes(v?.separator) ? v.separator : DEFAULT_SKU_FORMAT.separator;
  const order = SKU_ORDERS.find(([, o]) => JSON.stringify(o) === JSON.stringify(v?.order))?.[1] || DEFAULT_SKU_FORMAT.order;
  return { separator, order: [...order] };
}

/** A size's code in SKUs: the category's override if set, "One Size" → OS, otherwise the size label itself (spaces removed). */
export function sizeCodeFor(size: string, overrides?: Record<string, string>): string {
  const o = overrides?.[size]?.trim();
  if (o) return o.toUpperCase();
  if (/^one\s*size$/i.test(size.trim())) return "OS";
  return size.trim().replace(/\s+/g, "").toUpperCase();
}

export function buildSku(fmt: SkuFormat, productCode: string, colourCode: string, sizeCode: string): string {
  const part = { product: productCode.trim(), colour: colourCode.trim().toUpperCase(), size: sizeCode.trim().toUpperCase() };
  return fmt.order.map((p) => part[p]).join(fmt.separator);
}

/** EAN-13 / GTIN (8, 12, 13 or 14 digits) with a correct check digit. Returns an error message, or "" if valid. */
export function gtinProblem(value: string): string {
  const v = value.replace(/\s+/g, "");
  if (!/^\d+$/.test(v)) return "An EAN / GTIN has digits only.";
  if (![8, 12, 13, 14].includes(v.length)) return "An EAN-13 has 13 digits (GTIN-8, 12 and 14 are accepted too).";
  const digits = v.split("").map(Number);
  const check = digits.pop()!;
  // From the right (next to the check digit): weights 3, 1, 3, 1…
  const sum = digits.reverse().reduce((t, d, i) => t + d * (i % 2 === 0 ? 3 : 1), 0);
  const expected = (10 - (sum % 10)) % 10;
  return check === expected ? "" : `The check digit is wrong: it should end in ${expected}.`;
}

/** Split "One Size=OS, 2XL=XXL" into { "One Size": "OS", "2XL": "XXL" }. */
export function parseSizeCodes(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of text.split(",")) {
    const [k, v] = part.split("=").map((x) => x?.trim());
    if (k && v) out[k] = v.toUpperCase();
  }
  return out;
}
export const sizeCodesText = (codes?: Record<string, string>) => Object.entries(codes || {}).map(([k, v]) => `${k}=${v}`).join(", ");
